export interface YelpResult {
  businesses: YelpBusiness[];
  total: number;
}

export interface YelpBusiness {
  id: string;
  name: string;
  rating: number;
  price: string;
  address: string;
  phone: string;
  url: string;
  review_count: number;
  is_closed: boolean;
  categories: { alias: string; title: string }[];
  business_hours: {
    open: {
      is_overnight: boolean;
      start: string;
      end: string;
      day: number;
    }[];
    hours_type: string;
    is_open_now: boolean;
  }[];
}

export interface YelpQuery {
  term: string;
  categories: string[];
  price: string[];
  address: string;
  maxRadius: number;
  rating: number;
  minRatingCount: number;
  limit: number;
}

type ToastCallback = (message: string, type: "success" | "error") => void;

export class YelpAPI {
  private apiKey: string;
  private offset: number;
  private baseUrl: string;
  private currentResults: YelpResult;

  constructor(apiKey: string) {
    this.apiKey = apiKey;
    this.offset = 0;
    this.baseUrl = "https://api.yelp.com/v3/businesses/search";
    this.currentResults = { businesses: [], total: 0 };
  }

  private async fetchResults(
    query: YelpQuery,
    limit: number,
    toast: ToastCallback
  ): Promise<YelpResult> {
    let url = `${this.baseUrl}?location=${query.address}&limit=${limit}&offset=${this.offset}`;

    if (query.categories && query.categories.length > 0) {
      url += `&categories=${query.categories.join(",")}`;
    }
    if (query.price && query.price.length > 0) {
      url += `&price=${query.price
        .map((price) => price.length.toString())
        .join(",")}`;
    }

    if (query.term) {
      url += `&term=${query.term}`;
    }

    if (query.maxRadius) {
      const meters = Math.round(query.maxRadius * 1609.34);
      url += `&radius=${meters}`;
    }

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${this.apiKey}`,
        },
      });
      const data = await response.json();

      if (data.error) {
        toast("Error fetching results: " + data.error.description, "error");
        return { businesses: [], total: 0 };
      }

      const businesses: YelpBusiness[] = data.businesses.map((business) => ({
        id: business.id,
        name: business.name,
        url: business.url,
        is_closed: business.is_closed,
        image_url: business.image_url || "",
        address: business.location.display_address.join(", "),
        price: business.price || "",
        phone: business.display_phone || "",
        rating: business.rating ? business.rating : 0,
        review_count: business.review_count ? business.review_count : 0,
        categories: business.categories,
        business_hours: business.business_hours,
      }));

      // Filter closed, rating, rating count
      const filteredBusinesses = businesses.filter(
        (business) =>
          !business.is_closed &&
          business.rating >= query.rating &&
          business.review_count >= query.minRatingCount
      );

      return {
        businesses: filteredBusinesses,
        total: data.total,
      };
    } catch (error) {
      console.error("Error fetching results:", error);
      toast("Error fetching results: " + error, "error");
      return { businesses: [], total: 0 };
    }
  }

  public async search(
    query: YelpQuery,
    toast: ToastCallback
  ): Promise<YelpResult> {
    this.offset = 0;
    let accumulatedBusinesses: YelpBusiness[] = [];
    const MAX_RESULTS = 240;
    const effectiveLimit = Math.min(query.limit, MAX_RESULTS);

    while (accumulatedBusinesses.length < effectiveLimit) {
      const remaining = effectiveLimit - accumulatedBusinesses.length;
      const limit = Math.min(50, remaining, MAX_RESULTS - this.offset);

      if (limit === 0) {
        break;
      }

      const result = await this.fetchResults(query, limit, toast);
      accumulatedBusinesses = accumulatedBusinesses.concat(result.businesses);

      if (
        accumulatedBusinesses.length >= result.total ||
        result.businesses.length === 0
      ) {
        break; // No more pages left or no businesses returned
      }

      this.offset += limit;
    }

    this.currentResults = {
      businesses: accumulatedBusinesses.slice(0, effectiveLimit),
      total: accumulatedBusinesses.length,
    };

    return this.currentResults;
  }

  public exportToCSV(): string {
    if (!this.currentResults || this.currentResults.businesses.length === 0) {
      return "";
    }

    const headers = [
      "id",
      "name",
      "rating",
      "price",
      "address",
      "phone",
      "url",
      "review_count",
      "categories",
    ];

    const csvRows = this.currentResults.businesses.map((business) => {
      const categories = business.categories
        .map((category) => category.title)
        .join(";");
      return [
        business.id,
        business.name,
        business.rating,
        business.price,
        business.address,
        business.phone,
        business.url,
        business.review_count,
        categories,
      ]
        .map((value) => `"${value}"`)
        .join(",");
    });

    return [headers.join(","), ...csvRows].join("\n");
  }
}
